/**
 * PLP swatch swap — clicking a colour swatch updates the product card
 * in place (image, title, links) without navigating to a new page.
 * Handles both product-card (PLP) and pdp-carousel__card (carousel) containers.
 */

document.addEventListener('click', (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const swatch = /** @type {HTMLElement|null} */ (target.closest('.plp-swatches__item[data-swap-url]'));
  if (!swatch) return;

  const { swapUrl, swapTitle, swapImage, swapSrcset, swapHover, swapHoverSrcset } = swatch.dataset;
  if (!swapUrl) return;

  // ── Find card root — PLP product-card or carousel quick-buy-component ───
  // Campaign tiles (snippets/plp-content-tile.liquid) render the product card
  // under fixed campaign media, so swapping in place would mismatch it: their
  // swatches stay plain links to the other colourway.
  if (swatch.closest('.plp-content-tile')) return;

  const plpCard = swatch.closest('product-card');
  const carouselCard = swatch.closest('quick-buy-component.pdp-carousel__card');
  const card = plpCard ?? carouselCard;
  if (!card) return;

  e.preventDefault();

  // The title before the swap, so labels that embed it (the (+) aria-label is
  // a translated "Quick buy {{ title }}") can be rewritten without the JS
  // knowing the translation.
  let previousTitle = '';

  if (carouselCard) {
    // ── Carousel card image swap ───────────────────────────────────────────
    const imgContainer = carouselCard.querySelector('.pdp-carousel__card-img');
    const primaryImg = carouselCard.querySelector('.pdp-carousel__card-img-primary');
    let hoverImg = carouselCard.querySelector('.pdp-carousel__card-img-hover');

    if (primaryImg instanceof HTMLImageElement) {
      if (swapImage) primaryImg.src = swapImage;
      if (swapSrcset) primaryImg.srcset = swapSrcset;
      primaryImg.alt = swapTitle ?? primaryImg.alt;
    }

    if (swapHover && swapHover !== swapImage) {
      if (hoverImg instanceof HTMLImageElement) {
        hoverImg.src = swapHover;
        hoverImg.srcset = swapHoverSrcset ?? '';
      } else if (imgContainer) {
        // Create hover image if this card didn't have one
        const newHover = document.createElement('img');
        newHover.className = 'pdp-carousel__card-img-hover';
        newHover.src = swapHover;
        newHover.srcset = swapHoverSrcset ?? '';
        newHover.alt = '';
        newHover.loading = 'lazy';
        imgContainer.insertBefore(newHover, imgContainer.querySelector('.pdp-carousel__wishlist'));
      }
    } else if (hoverImg) {
      // Swapped product has no hover image — remove the element
      hoverImg.remove();
    }

    // ── Carousel title ─────────────────────────────────────────────────────
    const titleEl = carouselCard.querySelector('.pdp-carousel__card-title');
    if (titleEl) previousTitle = titleEl.textContent.trim();
    if (titleEl && swapTitle) titleEl.textContent = swapTitle;

  } else {
    // ── PLP card image swap ────────────────────────────────────────────────
    const gallery = card.querySelector('[ref="cardGallery"]');
    const slides = (gallery ?? card).querySelectorAll('slideshow-slide:not([hidden])');
    const swapSlide = (/** @type {Element} */ slide, /** @type {string|undefined} */ src, /** @type {string|undefined} */ srcset) => {
      const img = slide.querySelector('.product-media__image');
      if (img instanceof HTMLImageElement) {
        if (src) img.src = src;
        if (srcset) img.srcset = srcset;
        img.alt = swapTitle ?? img.alt;
      }
    };
    if (slides[0]) swapSlide(slides[0], swapImage, swapSrcset);
    if (slides[1]) swapSlide(slides[1], swapHover, swapHoverSrcset);

    // ── PLP title ──────────────────────────────────────────────────────────
    // The <p> directly: a card with no media also renders a placeholder
    // [ref="productTitleLink"] with a <span>, which would be found first.
    const titleEl = card.querySelector('[ref="productTitleLink"] p');
    if (titleEl) previousTitle = titleEl.textContent.trim();
    if (titleEl && swapTitle) titleEl.textContent = swapTitle;

    // ── Accessible names: the gallery link's aria-label and the hidden text
    // of the card's overlay link still named the pre-swap product ─────────
    if (swapTitle) {
      const galleryLink = card.querySelector('[ref="cardGalleryLink"]');
      if (galleryLink) galleryLink.setAttribute('aria-label', swapTitle);
      const hiddenTitle = card.querySelector('.product-card__link .visually-hidden');
      if (hiddenTitle) hiddenTitle.textContent = swapTitle;
    }
  }

  // ── All product links in the card (skip swatch links themselves) ─────────
  card.querySelectorAll('a[href]').forEach((a) => {
    if (!a.closest('.plp-swatches')) {
      a.setAttribute('href', swapUrl);
    }
  });

  // ── GA4 card payload — select_item and the add_to_cart lookup read these,
  // so they must follow the swapped product or analytics reports the
  // pre-swap colourway ─────────────────────────────────────────────────────
  if (swatch.dataset.swapProductId) card.dataset.productId = swatch.dataset.swapProductId;
  if (swatch.dataset.swapGtmItem) card.dataset.gtmItem = swatch.dataset.swapGtmItem;

  // ── Algolia search dropdown: the framework attributes a click to the
  // enclosing li[data-handle]. Point it at the product now shown; a handle
  // that was not one of the query's hits reports nothing, instead of
  // crediting the pre-swap colourway. Dropdown only: grid items are
  // attributed by data-object-id, and the framework restamps them by handle ──
  const resultItem = card.closest('[data-ls-predictive-products] > li[data-handle]');
  const swappedHandle = swapUrl.match(/\/products\/([^/?#]+)/)?.[1];
  if (resultItem && swappedHandle) resultItem.setAttribute('data-handle', swappedHandle);

  // ── (+) quick-add trigger — must follow the swapped product or the IRL
  // drawer opens (and adds) the pre-swap colourway ─────────────────────────
  card.querySelectorAll('[data-irl-trigger]').forEach((trigger) => {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.dataset.productUrl = swapUrl;
    const label = trigger.getAttribute('aria-label');
    if (label && previousTitle && swapTitle && label.includes(previousTitle)) {
      // Callback form: a plain replacement string treats $& and $' specially.
      trigger.setAttribute('aria-label', label.replace(previousTitle, () => swapTitle));
    }
  });

  // ── Quick buy sizes ─────────────────────────────────────────────────────
  // On carousel cards the card root IS the quick-buy-component — querySelector
  // only searches descendants, so check the root first.
  const qb = card.matches('quick-buy-component') ? card : card.querySelector('quick-buy-component');
  if (qb && typeof (/** @type {any} */ (qb)).resetSizes === 'function') {
    try {
      const variants = JSON.parse(swatch.dataset.swapVariants || '[]');
      /** @type {any} */ (qb).resetSizes(variants);
      // The component-level PIM id must follow the swapped product, or the
      // next add submits the OLD product's _pim_product_id.
      if (swatch.dataset.swapPim !== undefined) {
        qb.setAttribute('data-lc-pim', swatch.dataset.swapPim);
      }
      // Likewise the Algolia objectID (per colour: OP-{id}.{colour}), or the
      // purchase pixel attributes the swapped colour's sale to the original.
      // Cleared, not left, when the swapped product has none.
      if (swatch.dataset.swapObjectId !== undefined) {
        if (swatch.dataset.swapObjectId) qb.setAttribute('data-lc-object-id', swatch.dataset.swapObjectId);
        else qb.removeAttribute('data-lc-object-id');
      }
    } catch (_) { /* ignore */ }
  }

  // ── Active swatch state ─────────────────────────────────────────────────
  card.querySelectorAll('.plp-swatches__item').forEach((s) => {
    const isActive = s === swatch;
    s.classList.toggle('plp-swatches__item--active', isActive);
    if (isActive) {
      s.setAttribute('aria-current', 'page');
    } else {
      s.removeAttribute('aria-current');
    }
  });
});
